import * as React from "react";
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useTheme } from "../Utils/useTheme";
import { useSEO } from "../Utils/useSEO";
import { ScrollReveal, ScrollRevealItem } from "../components/ScrollReveal";
import {
  ShieldCheck,
  FileCheck2,
  GitPullRequest,
  ClipboardList,
  Truck,
  Clock,
  MapPin,
  Layers,
  ShieldAlert,
  CreditCard,
  Sparkles,
  DollarSign,
  Award,
  BarChart3,
  MessageSquare,
  Database,
  Cpu,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Helper to resolve icon
function getIcon(name, className = "w-6 h-6 text-brand-orange") {
  switch (name) {
    case "ShieldCheck": return <ShieldCheck className={className} />;
    case "FileCheck2": return <FileCheck2 className={className} />;
    case "GitPullRequest": return <GitPullRequest className={className} />;
    case "ClipboardList": return <ClipboardList className={className} />;
    case "Truck": return <Truck className={className} />;
    case "Clock": return <Clock className={className} />;
    case "MapPin": return <MapPin className={className} />;
    case "Layers": return <Layers className={className} />;
    case "ShieldAlert": return <ShieldAlert className={className} />;
    case "CreditCard": return <CreditCard className={className} />;
    case "Sparkles": return <Sparkles className={className} />;
    case "DollarSign": return <DollarSign className={className} />;
    case "Award": return <Award className={className} />;
    case "BarChart3": return <BarChart3 className={className} />;
    case "MessageSquare": return <MessageSquare className={className} />;
    case "Database": return <Database className={className} />;
    case "Cpu": return <Cpu className={className} />;
    default: return <Cpu className={className} />;
  }
}

// Module Data Lookup
const modulesData = {
  "compliance-verification": {
    title: "Compliance Verification",
    index: "02",
    desc: "Continuous audit tracks, real-time background scans, ESG reporting, and certificate expiration alerts.",
    longDesc: "Automate compliance tracking across your entire supply chain. Aether Core scans global registries, sanctions lists, and ESG databases continuously to flag certifications before they expire.",
    iconName: "ShieldCheck",
    category: "compliance",
    pillars: [
      { iconName: "ShieldCheck", title: "Real-Time Registry Checks", desc: "Scans official corporate databases daily to ensure registry records remain active and clear." },
      { iconName: "ShieldAlert", title: "Sanctions Scan Radar", desc: "Instantly checks entities against OFAC, EU, UN, and global trade sanction blacklists." },
      { iconName: "Award", title: "ESG Verification", desc: "Automates carbon score checking, supplier labor policies audits, and ESG credentials validation." },
      { iconName: "Clock", title: "Automated Expiry Gates", desc: "Proactively alerts sourcing agents 30 days before critical certificates reach expiry limits." }
    ],
    simulator: {
      title: "Real-Time Sanction & ESG Audit Scanner",
      buttonText: "Trigger Registry Verification Scan",
      metricTitle: "AUDIT CONFIDENCE",
      metricValue: "99.8% Match Accuracy",
      logs: [
        "Initializing compliance registry scanning adapters...",
        "Querying global sanctions registers (OFAC / EU / UN)...",
        "[OK] Identity validation succeeded. Zero sanctions matches found.",
        "Evaluating ESG carbon footprint acknowledgments...",
        "[OK] ESG policy statement validated. Emissions metrics within target parameters.",
        "Syncing results with Master Vendor Compliance indexes...",
        "Replication protocol complete. Verification record updated."
      ],
      outputs: [
        { label: "REGISTRY ID", value: "COMP-AUDIT-91B" },
        { label: "SANCTION STATE", value: "Clear & Compliant" },
        { label: "ESG SCORE", value: "A+ Tier Rating" },
        { label: "LAST SCAN", value: "Just Now" }
      ]
    }
  },
  "contract-management": {
    title: "Contract Management",
    index: "03",
    desc: "Digital secure repository with milestone triggers, SLA compliance registers, and automated renew alerts.",
    longDesc: "Manage and audit contracts with automated milestone tracking. Sync delivery logs directly to contract terms, enabling automated alert notifications and compliance audits.",
    iconName: "FileCheck2",
    category: "compliance",
    pillars: [
      { iconName: "FileCheck2", title: "Digital Secure Repository", desc: "Store, encrypt, and categorize multi-vendor contract sheets in one centralized repository." },
      { iconName: "Clock", title: "Automated Expiry Triggers", desc: "Triggers notifications 60 days before contract agreements reach their termination dates." },
      { iconName: "Layers", title: "SLA Metric Audit", desc: "Cross-checks actual delivery coordinates and invoices against contracted performance SLAs." },
      { iconName: "Lock", title: "Milestone Release Gates", desc: "Links ledger payment releases directly to contract milestone confirmations." }
    ],
    simulator: {
      title: "Contract Milestone Audit Pipeline",
      buttonText: "Verify Milestone Agreements",
      metricTitle: "SLA COMPLIANCE",
      metricValue: "98.4% Performance",
      logs: [
        "Initializing smart contract analysis pipeline...",
        "Loading master agreement terms: PO-CONTRACT-09...",
        "Cross-checking supplier dispatch logs against milestone index...",
        "[OK] Delivery records verify that milestones 1 & 2 have been completed.",
        "Validating SLA parameters (Average wait time, shipping transit speed)...",
        "[OK] Performance compliance metrics verify SLA levels match criteria.",
        "Generating cryptographic verification log for ledger sync...",
        "Contract audit pipeline success. Milestone release authorized."
      ],
      outputs: [
        { label: "CONTRACT ID", value: "PO-CON-8042" },
        { label: "MILESTONE STATE", value: "2/3 Confirmed" },
        { label: "ESCROW STATUS", value: "Fund Release Authorized" },
        { label: "AUDIT REGISTRY", value: "Synchronized" }
      ]
    }
  },
  "rfq-management": {
    title: "RFQ Management",
    index: "04",
    desc: "Automated RFQ generation, secure bid portals, multi-dimensional response comparison, and award workflows.",
    longDesc: "Create and publish RFQs automatically. The secure vendor portal allows suppliers to submit bids, while AI tools compile and compare metrics to recommend the optimal award.",
    iconName: "GitPullRequest",
    category: "logistics",
    pillars: [
      { iconName: "GitPullRequest", title: "Automated RFQ Dispatch", desc: "Triggers RFQ dispatch to vetted supplier pools based on inventory thresholds." },
      { iconName: "Lock", title: "Secure Bidding Channel", desc: "Suppliers submit bids through isolated channels to ensure transparency." },
      { iconName: "BarChart3", title: "Multi-Dim Comparison", desc: "Compares prices, shipping routes, delivery speed, and risk factors side by side." },
      { iconName: "CheckCircle2", title: "One-Click Award", desc: "Dispatches purchase orders automatically once an award is confirmed." }
    ],
    simulator: {
      title: "Autonomous RFQ Dispatch & Bid Comparison Engine",
      buttonText: "Run RFQ Award Algorithm",
      metricTitle: "OPTIMAL AWARD CONFIDENCE",
      metricValue: "95.6% Score",
      logs: [
        "Parsing inventory requisition trigger (Low-stock warning: Engines)...",
        "Generating dynamic RFQ specs: RFQ-ENG-802...",
        "Dispatching secure portals invite link to 5 verified suppliers...",
        "Ingesting incoming bids from McLaren, Orion, and Vanguard...",
        "AI comparison module analyzing: Pricing, lead time, and compliance...",
        "[OK] Optimal vendor selected: McLaren Engine Systems (Score: 97/100).",
        "Drafting PO-8042 with award parameters..."
      ],
      outputs: [
        { label: "RFQ RUN", value: "RFQ-ENG-802" },
        { label: "BIDS RECEIVED", value: "3 Verified" },
        { label: "WINNING BIDDER", value: "McLaren Systems" },
        { label: "EST. LEAD TIME", value: "48 Hours" }
      ]
    }
  },
  "purchase-orders": {
    title: "Purchase Orders",
    index: "05",
    desc: "Instant digital purchase orders matching compliance rules, multi-level approvals, and supplier confirm receipts.",
    longDesc: "Generate purchase orders instantly from inventory thresholds. Compliance validation checks matching rules and dispatches digital documents for supplier sign-off.",
    iconName: "ClipboardList",
    category: "logistics",
    pillars: [
      { iconName: "ClipboardList", title: "Auto-PO Generation", desc: "Integrates inventory triggers directly with purchase order dispatch systems." },
      { iconName: "ShieldCheck", title: "Compliance Check Gates", desc: "Validates PO fields against registry compliance rules before dispatch." },
      { iconName: "Layers", title: "Approval Hierarchies", desc: "Automates multi-level manager notifications based on order valuation." },
      { iconName: "MessageSquare", title: "Digital Confirmation", desc: "Supplier registers secure digital signature directly on PO receipts." }
    ],
    simulator: {
      title: "Purchase Order Validation Registry",
      buttonText: "Validate & Dispatch PO",
      metricTitle: "PO VALIDATION",
      metricValue: "100% Compliant",
      logs: [
        "Compiling PO-8042 requisitions: 150 engines for McLaren...",
        "Running compliance check: Validating McLaren profile registry status...",
        "[OK] McLaren compliance status active. ISO certificate validated.",
        "Verifying internal budget release authority limits...",
        "[OK] Budget release authorization verified (Treasury node).",
        "Signing PO document with cryptographic company key...",
        "Dispatching digital PO-8042 to supplier portal...",
        "[OK] Supplier signature handshake recorded."
      ],
      outputs: [
        { label: "PO NUMBER", value: "PO-8042" },
        { label: "BUDGET MATCH", value: "Authorized" },
        { label: "SUPPLIER STATUS", value: "Signed & Confirmed" },
        { label: "LEDGER STATUS", value: "Sync Complete" }
      ]
    }
  },
  "smart-delivery": {
    title: "Smart Delivery",
    index: "06",
    desc: "Intelligent freight routing, carrier dispatch verification, automated transit logs, and custom gate checkouts.",
    longDesc: "Optimize freight routing with real-time analytics. Verify carrier credentials, generate secure electronic bills of lading, and track transit logs to streamline gate check-in.",
    iconName: "Truck",
    category: "logistics",
    pillars: [
      { iconName: "Truck", title: "AI Route Optimization", desc: "Analyzes weather, port delays, and traffic to determine optimal routes." },
      { iconName: "ShieldCheck", title: "Carrier Verification", desc: "Scans driver credentials and vehicle transport permits at checkout." },
      { iconName: "FileCheck2", title: "Electronic Bills of Lading", desc: "Immutably records cargo logs, quantities, and custody changes." },
      { iconName: "Layers", title: "Yard Gate Pre-Auth", desc: "Pre-registers incoming shipments inside dock scheduling calendars." }
    ],
    simulator: {
      title: "Logistics Routing & Dispatch Validation System",
      buttonText: "Calculate Optimal Routing",
      metricTitle: "ROUTING EFFICIENCY",
      metricValue: "94.2% Optimization",
      logs: [
        "Loading logistics shipment manifest: SHIP-91A...",
        "Scanning real-time freight lane reports and port delay alerts...",
        "Determining optimal route: Lane 4 (Avoids Rotterdam port delays)...",
        "Generating secure electronic Bill of Lading (eBOL-81A)...",
        "[OK] Driver credentials and transport container plates validated.",
        "Broadcasting dispatch logs to warehouse dock check-in registers...",
        "Cargo dispatch active. Route telemetry active."
      ],
      outputs: [
        { label: "SHIPMENT ID", value: "SHIP-91A" },
        { label: "OPTIMAL ROUTE", value: "Lane 4 (Overland)" },
        { label: "eBOL STATE", value: "Signed & Locked" },
        { label: "ETA WINDOW", value: "12 Hours (Stable)" }
      ]
    }
  },
  "jit-logistics": {
    title: "JIT Logistics",
    index: "07",
    desc: "Synchronized factory-to-dock logistics grids to minimize warehouse storage time and maintain JIT metrics.",
    longDesc: "Coordinate factory logistics with warehouse schedules. Real-time slot adjustments minimize warehouse storage time, enabling strict just-in-time operations.",
    iconName: "Clock",
    category: "logistics",
    pillars: [
      { iconName: "Clock", title: "Factory-to-Dock Sync", desc: "Coordinates incoming logistics with real-time factory assembly demands." },
      { iconName: "Layers", title: "Dynamic Slot Scheduler", desc: "Re-sequences dock slot positions dynamically based on vehicle delays." },
      { iconName: "DollarSign", title: "Staging Minimization", desc: "Keeps staging times under 4 hours, lowering inventory storage costs." },
      { iconName: "MapPin", title: "Geofence Telemetry", desc: "Triggers check-in gates as cargo enters regional geofence zones." }
    ],
    simulator: {
      title: "JIT Factory-Dock Synchronization Pipeline",
      buttonText: "Synchronize Dock Slots",
      metricTitle: "DOCK DWELL TIMELINE",
      metricValue: "22 min Staging Average",
      logs: [
        "Analyzing assembly line schedule for tomorrow's production...",
        "Checking coordinates for incoming parts delivery vehicles...",
        "Truck T-912 delayed by 35 minutes on Interstate 40...",
        "Recalculating dock slots sequence: Swapping Slot 4 with Slot 7...",
        "[OK] Slot assignment updated. Production line bypass confirmed.",
        "Sending geofence alert threshold triggers to driver console...",
        "JIT synchronization complete. Yard slots successfully adjusted."
      ],
      outputs: [
        { label: "FACTORY FLOW", value: "On-Time (Stable)" },
        { label: "ACTIVE CARRIERS", value: "14 Traced" },
        { label: "SLOT SEQUENCE", value: "Re-Aligned" },
        { label: "STAGING COST", value: "-30% Optimized" }
      ]
    }
  },
  "gps-tracking": {
    title: "GPS Tracking",
    index: "08",
    desc: "Real-time vessel and cargo tracking with geofencing triggers and automated ETAs mapping terminal gates.",
    longDesc: "Follow ocean vessels and overland carriers. Geofencing triggers update ETA forecasts dynamically, notifying docking teams when delays happen.",
    iconName: "MapPin",
    category: "logistics",
    pillars: [
      { iconName: "MapPin", title: "Multi-Modal Coordinates", desc: "Traces maritime coordinates and truck transits on a single map." },
      { iconName: "Clock", title: "Predictive ETA Logic", desc: "Updates delivery windows dynamically based on historical bottlenecks." },
      { iconName: "ShieldAlert", title: "Geofence Breach Alerts", desc: "Flashes alerts when cargo leaves pre-designated transit paths." },
      { iconName: "Database", title: "Telemetry Ingestion", desc: "Ingests real-time API logs from cellular and satellite transponders." }
    ],
    simulator: {
      title: "Geofenced Telemetry & Predictive ETA Monitor",
      buttonText: "Check Telemetry Coordinates",
      metricTitle: "ETA ACCURACY",
      metricValue: "99.1% Confidence",
      logs: [
        "Querying transponder API for vessel V-802 (Indian Ocean)...",
        "[OK] Satellite handshake active. Position: 18.975° N, 72.825° E.",
        "Current speed: 18 knots. Weather status: Clear.",
        "Recalculating predictive ETA mapping: Destination Terminal Gates...",
        "Estimated check-in: 06-18 14:00 (Matches dock slot 2 reservation).",
        "Geofence trigger registered (100km boundary check: Clear)...",
        "Telemetry feed synchronized."
      ],
      outputs: [
        { label: "VESSEL/TRUCK", value: "Vessel V-802" },
        { label: "COORDINATES", value: "18.975°N, 72.825°E" },
        { label: "PREDICTED ETA", value: "June 18, 14:00" },
        { label: "GEOFENCE STATE", value: "Safe Transit" }
      ]
    }
  },
  "dock-management": {
    title: "Dock Management",
    index: "09",
    desc: "Dynamic yard slot reservations, check-in queuing, QR scanners, and slot congestion alerts.",
    longDesc: "Manage yard gate congestion with slot reservations. Drivers scan dynamic QR codes at arrival to receive instant dock assignments.",
    iconName: "Layers",
    category: "logistics",
    pillars: [
      { iconName: "Layers", title: "Dynamic Slot Reserver", desc: "Suppliers book slots online, syncing yard schedules automatically." },
      { iconName: "ShieldCheck", title: "QR Contactless Gate", desc: "Drivers scan dynamic passes to auto-clear security checkposts." },
      { iconName: "Clock", title: "Dwell Time Diagnostics", desc: "Measures wait times and sequences slots to optimize throughput." },
      { iconName: "ShieldAlert", title: "Yard Congestion Alerts", desc: "Flags gridlocks and alerts incoming drivers to delay check-in." }
    ],
    simulator: {
      title: "Yard Staging & QR Gate Clearance Controller",
      buttonText: "Authorize Staging Passes",
      metricTitle: "YARD UTILIZATION",
      metricValue: "88.5% Staged Capacity",
      logs: [
        "Scanning check-in queue at Gate 2...",
        "Ingesting QR Pass: QR-PASS-91A for Truck T-912...",
        "[OK] Pass authenticated. Supplier profile matches PO-8042.",
        "Locating empty dock slot: Checking occupancy statistics...",
        "Assigning Slot 4 (Available: Ready for immediate staging)...",
        "Sending gate clearance signal: Raising barrier...",
        "[OK] Truck T-912 cleared Gate 2. Dwell timer started."
      ],
      outputs: [
        { label: "ACTIVE PASS", value: "QR-PASS-91A" },
        { label: "ASSIGNED SLOT", value: "Dock Slot 4" },
        { label: "CLEARANCE STATE", value: "Gate Opened" },
        { label: "DWELL TIMER", value: "00:00:00 (Active)" }
      ]
    }
  },
  "exception-management": {
    title: "Exception Management",
    index: "10",
    desc: "AI diagnostics flagging delays, customs holds, or damage reports, with automated re-routing logic.",
    longDesc: "Diagnose and resolve supply chain delays automatically. AI workflows identify customs holds or weather delays to re-route incoming logistics pipelines.",
    iconName: "ShieldAlert",
    category: "logistics",
    pillars: [
      { iconName: "ShieldAlert", title: "Real-Time Delays Alarm", desc: "Flags delay anomalies inside regional tracking streams instantly." },
      { iconName: "ShieldCheck", title: "Customs Hold Scanner", desc: "Monitors custom clearance documents for regulatory validation holds." },
      { iconName: "Cpu", title: "AI Re-Routing Engine", desc: "Identifies weather detours and recommends alternative cargo paths." },
      { iconName: "GitPullRequest", title: "Contingency Dispatch", desc: "Autodrafts emergency POs to backup suppliers during critical failures." }
    ],
    simulator: {
      title: "AI Logistics Exception Diagnostic Suite",
      buttonText: "Diagnose Exception Event",
      metricTitle: "RESOLVED RATE",
      metricValue: "92.4% Auto Resolved",
      logs: [
        "Analyzing tracking coordinates stream for cargo manifest SHIP-91A...",
        "[WARN] Anomaly Detected: Shipment stationary for 8 hours at border checkpost.",
        "Querying customs API database: Log code flags missing tax registration.",
        "Parsing custom document checklist... Finding ISO validation mismatch.",
        "Initiating self-correcting routing: Submitting corrected tax metadata...",
        "[OK] Document verification accepted. Customs hold cleared.",
        "Resuming route tracking. Alert status: Normal."
      ],
      outputs: [
        { label: "EXCEPTION LOG", value: "EXC-CHECK-91" },
        { label: "CAUSE IDENTIFIED", value: "Border Hold (Tax Id)" },
        { label: "ACTION TAKEN", value: "Profile Sync Submitted" },
        { label: "ROUTE STATUS", value: "Transit Resumed" }
      ]
    }
  },
  "ap-automation": {
    title: "AP Automation",
    index: "11",
    desc: "Digital ledger invoice ingestion, compliance checks, tax validation, and centralized approval desks.",
    longDesc: "Ingest invoices directly into a secure digital ledger. Automated validation checks tax identifiers and coordinates multi-level manager approvals.",
    iconName: "CreditCard",
    category: "finance",
    pillars: [
      { iconName: "CreditCard", title: "Automated Ingestion", desc: "Extracts invoice metadata from emails, PDFs, and XML sheets." },
      { iconName: "ShieldCheck", title: "Tax Registry Validation", desc: "Verifies tax identification details against governmental tax records." },
      { iconName: "Lock", title: "Ledger Pre-Approval", desc: "Validates ledger balance checks before dispatching funding requests." },
      { iconName: "Layers", title: "Centralized Approval Desk", desc: "Routes approval notifications directly to designated financial managers." }
    ],
    simulator: {
      title: "Digital Invoice Ingestion & Validation Core",
      buttonText: "Ingest Invoice Ledger",
      metricTitle: "EXTRACTION RATE",
      metricValue: "99.9% Extraction Match",
      logs: [
        "Ingesting digital invoice PDF: INV-9102 from Brigade...",
        "Running OCR parsing: Extracting values, line items, and tax metadata...",
        "Checking tax registry: Validating Brigade's VAT registration status...",
        "[OK] Tax ID matches official register records.",
        "Comparing invoice value ($45,000) against purchase order PO-2024...",
        "[OK] Valuation match validated (Rule tolerance: 0.0%).",
        "Routing approval slip to AP Treasury desk..."
      ],
      outputs: [
        { label: "INVOICE NUMBER", value: "INV-9102" },
        { label: "VENDOR IDENTIFIED", value: "BRIGADE Systems" },
        { label: "VALUATION MATCH", value: "$45,000 (Approved)" },
        { label: "LEDGER STATE", value: "Ready for Payout" }
      ]
    }
  },
  "ai-threeway-match": {
    title: "AI 3-Way Match",
    index: "12",
    desc: "Autonomous cross-checking matching Purchase Orders, Delivery Notes, and Invoices with discrepancy detection.",
    longDesc: "Match purchase orders, delivery notes, and supplier invoices automatically. AI audits detect unit mismatches, quantity offsets, or pricing exceptions.",
    iconName: "Sparkles",
    category: "finance",
    pillars: [
      { iconName: "Sparkles", title: "PO-DN-Invoice Sync", desc: "Correlates purchase orders, delivery notes, and invoices automatically." },
      { iconName: "ShieldAlert", title: "Discrepancy Alarm", desc: "Highlights unit price offsets or quantity mismatches in real-time." },
      { iconName: "Clock", title: "Tolerance Margins", desc: "Approves matches automatically when discrepancies fall within thresholds." },
      { iconName: "Database", title: "Blockchain Matching Logs", desc: "Records verified verification logs in secure audit sheets." }
    ],
    simulator: {
      title: "Autonomous 3-Way Document Match Engine",
      buttonText: "Run 3-Way Audit Match",
      metricTitle: "MATCH VERIFICATION",
      metricValue: "0.0% Mismatch Variance",
      logs: [
        "Initializing 3-way matching protocol...",
        "Retrieving source documents: PO-8042, DN-9012, INV-9102...",
        "Evaluating item quantities: PO (150 engines) vs DN (150 engines) vs INV (150 engines)...",
        "[OK] Quantity levels match perfectly across all 3 documents.",
        "Evaluating unit prices: PO ($1,200) vs Invoice ($1,200)...",
        "[OK] Pricing matches contract terms. Discrepancy: 0.0%.",
        "Writing verification metadata to AP Ledger audit trail...",
        "3-Way match audit validated. Payout release authorized."
      ],
      outputs: [
        { label: "AUDIT EVENT", value: "AUDIT-3WAY-924" },
        { label: "PO / DN MATCH", value: "150 Units (Match)" },
        { label: "PRICING VERIFIED", value: "100% Correct" },
        { label: "AUDIT RECORD", value: "Written to Blockchain" }
      ]
    }
  },
  "payment-automation": {
    title: "Payment Automation",
    index: "13",
    desc: "Secure escrow releases, dynamic early payment discount schedules, and multi-currency global transfers.",
    longDesc: "Automate payouts to global suppliers. Complete transactions through secure escrow channels, apply dynamic discount rates, and initiate bank transfers.",
    iconName: "DollarSign",
    category: "finance",
    pillars: [
      { iconName: "Lock", title: "Escrow Release Gates", desc: "Dispatches payment releases directly upon verified cargo arrivals." },
      { iconName: "DollarSign", title: "Dynamic Discounting", desc: "Calculates discount offers for suppliers requesting early payment." },
      { iconName: "Globe", title: "Multi-Currency Transfers", desc: "Interfaces with international banking networks for global payouts." },
      { iconName: "FileCheck2", title: "Ledger Payout Proof", desc: "Records transaction hashes on the ledger to ensure traceability." }
    ],
    simulator: {
      title: "AP Escrow Payout & Bank Routing Scheduler",
      buttonText: "Schedule Escrow Payout",
      metricTitle: "TRANSACTION STATE",
      metricValue: "100% Release Secured",
      logs: [
        "Verifying delivery note signature (DN-9012 verification status: Complete)...",
        "Triggering AP Escrow payout release validation protocols...",
        "Checking supplier payment details (Vanguard Logistics)...",
        "Applying dynamic discounting formula (2/10 Net 30: 2% early discount)...",
        "[OK] Discount accepted: Saving $3,600 on INV-9102 payout.",
        "Routing payment dispatch through regional wire networks (INR sync)...",
        "[OK] Wire reference generated: AP-WIRE-83021. Payout released."
      ],
      outputs: [
        { label: "PAYMENT ID", value: "AP-WIRE-83021" },
        { label: "RECIPIENT VENDOR", value: "Vanguard Logistics" },
        { label: "DISCOUNT APPLIED", value: "$3,600 (Saved)" },
        { label: "TRANSACTION HASH", value: "0x83A41B092F0" }
      ]
    }
  },
  "supplier-scorecards": {
    title: "Supplier Scorecards",
    index: "14",
    desc: "Multi-dimensional performance matrices evaluating delivery times, invoice accuracy, and compliance.",
    longDesc: "Review supplier metrics automatically. Evaluates on-time delivery rates, invoice matches, and compliance checks to generate real-time supplier grade scorecards.",
    iconName: "Award",
    category: "compliance",
    pillars: [
      { iconName: "Clock", title: "Delivery SLA Analysis", desc: "Measures dock scheduling delay averages across regional operations." },
      { iconName: "Sparkles", title: "Invoice Accuracy Index", desc: "Tracks 3-way match pass rates to grade invoice billing accuracy." },
      { iconName: "ShieldCheck", title: "Compliance Scorecard", desc: "Maintains active verification records of certificates and carbon targets." },
      { iconName: "Award", title: "Supplier Tiering Index", desc: "Assigns tier ratings (Gold/Silver/Bronze) to optimize supplier grids." }
    ],
    simulator: {
      title: "Supplier Performance SLA & Scorecard Generator",
      buttonText: "Calculate Performance Grade",
      metricTitle: "VENDOR RATING",
      metricValue: "A+ Gold Vendor",
      logs: [
        "Querying delivery history for supplier: Vanguard Logistics...",
        "Average delay time: 14 minutes (Threshold: 30 minutes) -> Grade: A.",
        "Evaluating billing history: 24 match audits, 0 billing exceptions -> Grade: A+.",
        "Checking compliance status checks: Zero expired certifications -> Grade: A.",
        "Compiling performance matrix rating index...",
        "[OK] Overall composite grade generated: 96.5% (A+ Tier ranking).",
        "Updating supplier master index record."
      ],
      outputs: [
        { label: "SUPPLIER GRADE", value: "96.5% (A+)" },
        { label: "ON-TIME RATE", value: "98.2% (Excellent)" },
        { label: "MATCH ACCURACY", value: "100.0% (Zero Errors)" },
        { label: "TIER ASSIGNMENT", value: "Gold Tier Vendor" }
      ]
    }
  },
  "analytics": {
    title: "Analytics",
    index: "15",
    desc: "Centralized category spend dashboards, risk maps, bottlenecks analyzer, and automated auditor logs.",
    longDesc: "Analyze category spend structures, regional risk logs, and yard dwell time averages through interactive data charts and compliance dashboards.",
    iconName: "BarChart3",
    category: "ai",
    pillars: [
      { iconName: "BarChart3", title: "Spend Category Chart", desc: "Analyzes supplier payments to slice spending by category." },
      { iconName: "ShieldAlert", title: "Regional Risk Grading", desc: "Maps geopolitics, port delays, and weather constraints to score routes." },
      { iconName: "Layers", title: "Dwell Time Diagnostics", desc: "Pinpoints staging delays to optimize yard throughput." },
      { iconName: "FileCheck2", title: "Auditing Export Logs", desc: "Generates secure reports to support auditing pipelines." }
    ],
    simulator: {
      title: "Operational Spend & Congestion Diagnostics Engine",
      buttonText: "Generate Analytics Report",
      metricTitle: "ANALYTICS INTEGRITY",
      metricValue: "100% Data Sync",
      logs: [
        "Connecting to central transaction database index...",
        "Compiling category spend totals: Logistics (40%), Materials (50%), Tech (10%)...",
        "Analyzing yard dwell bottlenecks: Yard 2 reports high staging delays...",
        "Scanning route risk matrices: Ocean freight lane 3 flagged with storm advisory...",
        "Synthesizing composite spend and risk logs report...",
        "[OK] Operational analytics dashboards generated successfully.",
        "Exporting audit ledger records."
      ],
      outputs: [
        { label: "TOTAL TRANSACTED", value: "$4.8M (Current Q)" },
        { label: "BOTTLE-NECK YARD", value: "Yard 2 (Dwell: 45m)" },
        { label: "FLAGGED ROUTES", value: "Lane 3 (Ocean)" },
        { label: "EXPORT STATUS", value: "PDF/CSV Generated" }
      ]
    }
  },
  "communication-portal": {
    title: "Communication Portal",
    index: "16",
    desc: "Unified messaging thread with automatic translations, audit-trail locks, and shared document directories.",
    longDesc: "A secure, auditable workspace for supplier discussions. Features automatic language translations and immutable log threads for dispute resolution.",
    iconName: "MessageSquare",
    category: "ai",
    pillars: [
      { iconName: "MessageSquare", title: "Audit Message Logs", desc: "Maintains tamper-proof history of communication records." },
      { iconName: "Globe", title: "Real-Time Translation", desc: "Translates discussions dynamically to eliminate language gaps." },
      { iconName: "FileCheck2", title: "Shared Document Vault", desc: "Enables secure sharing of invoices, DNs, and tax sheets." },
      { iconName: "ShieldAlert", title: "Exception Event Links", desc: "Links discussions directly to dock delay and exception registers." }
    ],
    simulator: {
      title: "Auditable Dispute Translation Portal",
      buttonText: "Translate & Archive Thread",
      metricTitle: "TRANSLATION ACCURACY",
      metricValue: "99.4% Fluent Match",
      logs: [
        "Retrieving message thread for cargo exception EXC-CHECK-91...",
        "Supplier message (Mandarin): '货物已卡在边境，正在更新税务登记证。'...",
        "AI translation engine parsing syntax...",
        "[OK] English translation: 'Cargo is stuck at border, updating tax registration.'...",
        "Archiving translation matching index into audit-log records...",
        "Notifying logistics manager console of exception clarification...",
        "Dispute discussion synced."
      ],
      outputs: [
        { label: "MESSAGE THREAD", value: "EXC-CHECK-91 Logs" },
        { label: "SOURCE LANG", value: "Mandarin (zh)" },
        { label: "TARGET LANG", value: "English (en)" },
        { label: "AUDIT STAMP", value: "Archived & Sealed" }
      ]
    }
  },
  "erp-integration": {
    title: "ERP Integration",
    index: "17",
    desc: "Real-time sync adapters with SAP (S/4HANA), Oracle, Microsoft Dynamics 365, and major proprietary systems.",
    longDesc: "Connect your procurement command center to enterprise resource planning cores. Sync vendor profiles, PO registers, and ledger balances seamlessly.",
    iconName: "Database",
    category: "ai",
    pillars: [
      { iconName: "Database", title: "SAP S/4HANA Connector", desc: "Bi-directional vendor synchronization keeps profiles aligned." },
      { iconName: "FileCheck2", title: "Oracle Cloud Adapter", desc: "Syncs PO requisitions directly to procurement ledgers." },
      { iconName: "DollarSign", title: "Microsoft Dynamics Sync", desc: "Aligns payment accounts and ledger balances across portals." },
      { iconName: "Cpu", title: "REST API Webhooks", desc: "Supports real-time webhooks for custom corporate ERP systems." }
    ],
    simulator: {
      title: "Multi-ERP Data Sync Pipeline",
      buttonText: "Initiate Bi-Directional Sync",
      metricTitle: "REPLICATION STATUS",
      metricValue: "0ms Sync Lag",
      logs: [
        "Establishing data handshake with SAP instance (S/4HANA)...",
        "[OK] Handshake verified. Secure VPN connection active.",
        "Replicating verified vendor profile: SUPP-NODE-83A41...",
        "SAP database registers updated. Matching UEN: 2024-VANGUARD.",
        "Syncing purchase order tables with Oracle Cloud database...",
        "[OK] Oracle requisitions tables synchronized.",
        "Broadcasting confirmation log to ERP dashboard. Replication complete."
      ],
      outputs: [
        { label: "SAP CONNECTION", value: "Connected (Live)" },
        { label: "ORACLE DATA", value: "Synchronized" },
        { label: "RECORD COPIES", value: "2 Replicated" },
        { label: "VPN INTEGRITY", value: "99.9% Encrypted" }
      ]
    }
  },
  "ai-copilot": {
    title: "AI Copilot",
    index: "18",
    desc: "Conversational copilot (Aether Core) assisting with risk forecasting, PO approvals, and operational queries.",
    longDesc: "An intelligent conversational assistant (Aether Core) that alerts teams of cargo exceptions, generates audit summaries, and prompts payment releases.",
    iconName: "Cpu",
    category: "ai",
    pillars: [
      { iconName: "Cpu", title: "Predictive Risk Alerting", desc: "Forecasts supply anomalies before they affect assembly lines." },
      { iconName: "FileCheck2", title: "Automated RFQ Drafts", desc: "Generates RFQ specs based on low inventory warning triggers." },
      { iconName: "ShieldCheck", title: "Audit Summarizer", desc: "Generates clear checklists from long contract documents." },
      { iconName: "MessageSquare", title: "Conversational Interface", desc: "Answers supplier queries and initiates manager approval flows." }
    ],
    simulator: {
      title: "Aether Core AI Agent Diagnostics Simulator",
      buttonText: "Consult Aether AI Agent",
      metricTitle: "RESPONSE TIME",
      metricValue: "180ms Latency",
      logs: [
        "Aether Core listening for queries: Ingesting prompt...",
        "Prompt: 'Generate exception audit for PO-8042'...",
        "Scanning logistics exceptions registry logs...",
        "Analyzing delay records for McLaren Engine Systems (PO-8042)...",
        "[OK] Analysis complete. 1 minor staging delay resolved at Gate 2.",
        "Emitting summary report: 'PO-8042 is on track. Risk grade: 96/100.'...",
        "Emitting confirmation log to dashboard."
      ],
      outputs: [
        { label: "AI AGENT", value: "Aether Core (Active)" },
        { label: "DIAGNOSIS RUN", value: "PO-8042 Audit" },
        { label: "RISK PREDICTION", value: "Low Risk (96/100)" },
        { label: "SUGGESTED ACTION", value: "Release Escrow" }
      ]
    }
  }
};

export default function DynamicModulePage() {
  const { theme } = useTheme();
  const isDark = theme !== "light";
  const { slug } = useParams();
  
  // Find matching module configuration
  const moduleInfo = modulesData[slug || ""];

  useSEO({
    title: moduleInfo ? `${moduleInfo.title} // Detailed Capability` : "Detailed Capability",
    description: moduleInfo ? moduleInfo.desc : "Read about detailed capabilities of Supply Spot modules."
  });

  // Simulator State
  const [currentStep, setCurrentStep] = useState(1);
  const [auditProgress, setAuditProgress] = useState(0);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    if (isSimulating && moduleInfo) {
      setAuditProgress(0);
      setAuditLogs([]);
      const logs = moduleInfo.simulator.logs;
      let logIndex = 0;
      const interval = setInterval(() => {
        if (logIndex < logs.length) {
          const nextLog = logs[logIndex];
          setAuditLogs(prev => [...prev, nextLog]);
          setAuditProgress(Math.round(((logIndex + 1) / logs.length) * 100));
          logIndex++;
        } else {
          clearInterval(interval);
          setIsSimulating(false);
          setCurrentStep(3); // Go to finished registry state
        }
      }, 700);
      return () => clearInterval(interval);
    }
  }, [isSimulating, moduleInfo]);

  // If slug is invalid or not in record, show not found page
  if (!moduleInfo) {
    return (
      <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 flex flex-col justify-between">
        <Navbar />
        <main className="max-w-7xl mx-auto px-6 pt-36 pb-24 text-center flex-1 flex flex-col items-center justify-center gap-6">
          <AlertCircle className="w-16 h-16 text-brand-orange animate-pulse" />
          <h1 className="text-3xl font-bold font-title">Module Capability Not Found</h1>
          <p className="text-text-secondary text-sm max-w-md">
            The capability module you requested does not exist or has not been configured in the Capabilities Index.
          </p>
          <Link
            to="/features"
            className="px-6 py-3 bg-brand-orange hover:bg-brand-amber font-bold text-xs tracking-wider rounded-lg text-white hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 shadow-md shadow-brand-orange/15"
          >
            Return to Capabilities Index
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary">
      <Navbar />

      {/* Grid Background Overlay */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Ambient Orbs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-brand-orange/5 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-brand-amber/5 rounded-full blur-[140px] -z-10 pointer-events-none" />

      <main className="max-w-7xl mx-auto px-6 pt-36 pb-24 relative z-10">
        
        {/* Breadcrumb and Back Action */}
        <ScrollReveal className="self-start text-left mb-8">
          <Link
            to="/features"
            className="inline-flex items-center gap-2 text-xs font-semibold text-text-secondary hover:text-brand-orange transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Capabilities</span>
          </Link>
        </ScrollReveal>

        {/* HERO SECTION */}
        <section className="flex flex-col lg:flex-row items-center gap-12 mb-24">
          <ScrollReveal className="flex-grow flex-shrink-0 lg:w-1/2 text-left flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-semibold uppercase tracking-wider self-start shadow-md">
              <Sparkles className="w-3.5 h-3.5" />
              <span>MODULE {moduleInfo.index}. DETAILED CAPABILITY</span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-text-primary leading-tight font-title">
              {moduleInfo.title.split(" ").slice(0, -1).join(" ")} <br />
              <span className="bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold bg-clip-text text-transparent drop-shadow-md">
                {moduleInfo.title.split(" ").slice(-1).join(" ")}
              </span>
            </h1>

            <p className="text-text-secondary text-sm md:text-base leading-relaxed max-w-xl">
              {moduleInfo.longDesc}
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-2">
              <button
                onClick={() => {
                  const el = document.getElementById("simulator-section");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="px-6 py-3.5 font-bold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              >
                RUN PIPELINE SIMULATOR
              </button>
              <Link
                to="/contact"
                className={`px-6 py-3.5 font-bold text-xs tracking-wider rounded-lg bg-transparent border transition-all duration-300 ${isDark
                    ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                    : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                  }`}
              >
                REQUEST CUSTOM ERP BUILD
              </Link>
            </div>
          </ScrollReveal>

          {/* Right Hero Graphic */}
          <ScrollReveal delay={0.15} className="flex-grow lg:w-1/2 w-full h-[320px]">
            <div className="w-full h-full glass-panel rounded-3xl relative overflow-hidden shadow-2xl flex flex-col justify-center p-8 text-left border border-brand-orange/15 bg-gradient-to-tr from-bg-secondary/40 via-bg-primary/20 to-bg-secondary/10">
              <div className="absolute top-4 left-4 flex items-center gap-2 px-2.5 py-1 bg-bg-primary/80 border border-brand-orange/20 rounded-full">
                <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
                <span className="text-[9px] font-title font-bold text-text-primary tracking-wider uppercase">
                  AETHER VERIFICATION FLOW
                </span>
              </div>

              <div className="flex flex-col gap-4 mt-6">
                <div className="p-4 bg-bg-secondary/60 border border-text-muted/10 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getIcon(moduleInfo.iconName)}
                    <div>
                      <div className="text-[10px] text-text-secondary font-bold uppercase tracking-wider">MODULE STREAM</div>
                      <div className="text-xs font-bold text-text-primary">{moduleInfo.title} Service</div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-brand-amber bg-brand-orange/10 px-2 py-0.5 rounded border border-brand-orange/20">LIVE</span>
                </div>

                <div className="p-4 bg-bg-secondary/60 border border-text-muted/10 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-5 h-5 text-brand-amber" />
                    <div>
                      <div className="text-[10px] text-text-secondary font-bold uppercase tracking-wider">DIAGNOSTICS & SYNC</div>
                      <div className="text-xs font-bold text-text-primary">Enterprise Core Replication</div>
                    </div>
                  </div>
                  <span className="text-[9px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">READY</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </section>

        {/* CORE PILLARS SECTION */}
        <section className="mb-24">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold font-title text-text-primary mb-4">
              Structural Pillars of {moduleInfo.title}
            </h2>
            <p className="text-text-secondary text-xs sm:text-sm">
              We design our components to completely bypass manual screening workflows and maximize autonomous synchronization.
            </p>
          </ScrollReveal>

          <ScrollReveal staggerChildren={0.06} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {moduleInfo.pillars.map((pillar, idx) => (
              <ScrollRevealItem key={idx} className="flex">
                <div className="glass-panel p-6 rounded-2xl border border-brand-orange/10 hover:border-brand-amber/35 transition-all duration-300 flex flex-col gap-4 text-left w-full relative group">
                  {/* <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-brand-orange to-brand-amber opacity-0 group-hover:opacity-100 transition-opacity" /> */}
                  <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center text-brand-orange">
                    {getIcon(pillar.iconName, "w-5 h-5 text-brand-orange")}
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-text-primary mb-2">{pillar.title}</h3>
                    <p className="text-text-secondary text-xs leading-relaxed">{pillar.desc}</p>
                  </div>
                </div>
              </ScrollRevealItem>
            ))}
          </ScrollReveal>
        </section>

        {/* INTERACTIVE SIMULATOR */}
        <section id="simulator-section" className="mb-24 border-y border-border-theme py-20 bg-bg-secondary/40">
          <div className="max-w-4xl mx-auto">
            
            <ScrollReveal className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-bold uppercase tracking-wider mb-6">
                <Cpu className="w-3.5 h-3.5" />
                <span>SANDBOX SIMULATOR</span>
              </div>
              <h2 className="text-3xl font-bold mb-4 text-text-primary font-title">
                {moduleInfo.title} Sandbox
              </h2>
              <p className="text-text-secondary text-xs sm:text-sm">
                Run our simulated compliance audit tool to step through database registers, coordinate telemetry trackers, or trigger ledger synchronizations.
              </p>
            </ScrollReveal>

            <ScrollReveal className="glass-panel p-8 shadow-2xl relative border border-brand-orange/15 rounded-3xl">
              
              {/* Simulator Steps Nav */}
              <div className="flex justify-between items-center mb-10 pb-6 border-b border-border-theme">
                {[
                  { step: 1, label: "Setup" },
                  { step: 2, label: "Execution" },
                  { step: 3, label: "Verification" }
                ].map((s) => (
                  <div key={s.step} className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs transition-all ${
                        currentStep === s.step
                          ? "bg-brand-orange text-white shadow-glow-orange scale-105"
                          : currentStep > s.step
                          ? "bg-emerald-500 text-white"
                          : "bg-bg-secondary border border-border-theme text-text-secondary"
                      }`}
                    >
                      {currentStep > s.step ? <CheckCircle className="w-4 h-4" /> : s.step}
                    </div>
                    <span
                      className={`text-xs font-bold font-title tracking-wider hidden sm:inline ${
                        currentStep === s.step ? "text-brand-orange" : "text-text-secondary"
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Steps Body */}
              <div className="min-h-[250px] flex flex-col justify-between">
                <AnimatePresence mode="wait">
                  
                  {/* Step 1: Sandbox Configuration */}
                  {currentStep === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex flex-col gap-6 text-left"
                    >
                      <div>
                        <h3 className="font-bold text-sm text-text-primary mb-1">Step 1: Configure Sandbox Parameters</h3>
                        <p className="text-xs text-text-secondary">Input details for the simulated supplier profile registry.</p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs text-text-secondary font-semibold">Simulated Supplier Node</label>
                          <div className="px-4 py-2.5 bg-bg-primary border border-border-theme rounded-lg text-xs text-text-primary">
                            Vanguard Logistics
                          </div>
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs text-text-secondary font-semibold">Active Ledger</label>
                          <div className="px-4 py-2.5 bg-bg-primary border border-border-theme rounded-lg text-xs text-text-primary">
                            Paytm Escrow Ledger Sync
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-brand-orange/5 border border-brand-orange/30 rounded-xl">
                        <div className="flex items-center gap-2.5 text-brand-orange mb-1">
                          <Sparkles className="w-4 h-4" />
                          <span className="text-xs font-bold font-title">Aether Core AI Optimization Enabled</span>
                        </div>
                        <p className="text-[10px] text-text-secondary leading-relaxed">
                          Automated discrepancy correction and real-time ledger replication are active for this sandbox run.
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          setCurrentStep(2);
                          setIsSimulating(true);
                        }}
                        className="mt-4 px-6 py-3 bg-brand-orange hover:bg-brand-amber font-bold text-xs tracking-wider rounded-lg text-white transition-all flex items-center gap-1.5 shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30"
                      >
                        <span>{moduleInfo.simulator.buttonText}</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )}

                  {/* Step 2: Simulation Audit Logs */}
                  {currentStep === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex flex-col gap-6 text-left"
                    >
                      <div>
                        <h3 className="font-bold text-sm text-text-primary mb-1">Step 2: Aether Core Integrity Pipeline</h3>
                        <p className="text-xs text-text-secondary">Watch real-time verification adapters audit data nodes.</p>
                      </div>

                      <div className="bg-slate-950 border border-brand-orange/20 rounded-xl p-4 font-mono text-[10px] text-slate-300 min-h-[160px] flex flex-col gap-2 overflow-y-auto max-h-[220px]">
                        {auditLogs.map((log, idx) => (
                          <div key={idx} className="flex gap-2">
                            <span className="text-brand-orange font-bold">&gt;</span>
                            <span className={log?.includes("[WARN]") ? "text-brand-amber font-semibold" : log?.includes("[OK]") ? "text-emerald-500" : ""}>
                              {log}
                            </span>
                          </div>
                        ))}
                        {auditProgress < 100 && (
                          <div className="flex items-center gap-1.5 text-brand-orange animate-pulse">
                            <span>●</span>
                            <span>Adapters scanning registers...</span>
                          </div>
                        )}
                      </div>

                      <div className="w-full bg-bg-secondary rounded-full h-2 overflow-hidden border border-border-theme">
                        <div
                          className="bg-gradient-to-r from-brand-orange to-brand-amber h-full transition-all duration-500"
                          style={{ width: `${auditProgress}%` }}
                        />
                      </div>

                      <div className="flex justify-between items-center mt-4 text-[10px] font-bold text-text-secondary">
                        <span>Pipeline Progress: {auditProgress}%</span>
                      </div>
                    </motion.div>
                  )}

                  {/* Step 3: Verified Registry Entry */}
                  {currentStep === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="flex flex-col items-center text-center gap-6"
                    >
                      <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500 flex items-center justify-center text-emerald-500 shadow-lg shadow-emerald-500/10 animate-bounce">
                        <CheckCircle2 className="w-10 h-10" />
                      </div>

                      <div className="text-center">
                        <h3 className="font-bold text-lg text-text-primary mb-1">Pipeline Execution Success</h3>
                        <p className="text-xs text-text-secondary max-w-md mx-auto">
                          {moduleInfo.title} task completed and verified. Encrypted record sync is active.
                        </p>
                      </div>

                      {/* Diagnostic Summary */}
                      <div className="w-full max-w-md bg-bg-secondary/40 border border-border-theme p-5 rounded-2xl text-left grid grid-cols-2 gap-4 text-xs">
                        {moduleInfo.simulator.outputs.map((out, index) => (
                          <div key={index}>
                            <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">{out.label}</div>
                            <div className="text-xs font-bold text-text-primary mt-0.5">{out.value}</div>
                          </div>
                        ))}
                        <div>
                          <div className="text-[9px] text-text-secondary font-bold uppercase tracking-wider">{moduleInfo.simulator.metricTitle}</div>
                          <div className="text-xs font-bold text-emerald-500 mt-0.5">{moduleInfo.simulator.metricValue}</div>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setCurrentStep(1);
                          setAuditProgress(0);
                          setAuditLogs([]);
                        }}
                        className={`px-6 py-2.5 bg-transparent border transition-all duration-300 rounded-xl text-xs font-bold ${isDark
                            ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                            : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                          }`}
                      >
                        Reset Simulator Pipeline
                      </button>
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>

            </ScrollReveal>
          </div>
        </section>

        {/* ENTERPRISE ERP CAPABILITIES DETAILS */}
        <section className="max-w-4xl mx-auto mb-20 text-left flex flex-col gap-8">
          <ScrollReveal>
            <h2 className="text-2xl sm:text-3xl font-bold font-title mb-6">Detailed Module Architecture</h2>
            <p className="text-text-secondary text-xs sm:text-sm leading-relaxed mb-6">
              When this capability is integrated inside your enterprise core, Aether Core schedules background jobs to sync tables continuously. Missing files or data exceptions trigger alerts to ensure complete ledger compliance.
            </p>
          </ScrollReveal>
        </section>

        {/* FINAL CTA SECTION */}
        <ScrollReveal>
          <div className="glass-panel p-10 md:p-12 rounded-3xl border border-brand-orange/15 bg-gradient-to-r from-bg-secondary/40 to-bg-primary relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 mt-20">
            <div className="max-w-xl text-left">
              <h2 className="text-xl md:text-2xl font-title font-bold mb-3">Integrate with your procurement core?</h2>
              <p className="text-text-secondary text-xs md:text-sm leading-relaxed">
                Empower your sourcing team by integrating our self-service validation layers into your existing SAP instance or proprietary database.
              </p>
            </div>
            <Link
              to="/contact"
              className="px-6 py-3.5 font-semibold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 whitespace-nowrap"
            >
              SCHEDULE INTEGRATION WALKTHROUGH
            </Link>
          </div>
        </ScrollReveal>

      </main>
      <Footer />
    </div>
  );
}
